import type { CapFilter, CapId, CapKind, GrantedCap } from "./capabilities.js";
import type { Node } from "./nodes.js";

/** What an app is told about the frame it is rendering into (§5.2 `mount`). */
export type WindowInfo = {
  id: string;
  appId: string;
  w: number;
  h: number;
};

export type LogLevel = "debug" | "info" | "warn" | "error";
export const LOG_LEVELS: ReadonlySet<string> = new Set<string>([
  "debug",
  "info",
  "warn",
  "error",
]);

/** app → shell */
export type AppMessage =
  | { t: "render"; tree: Node }
  | { t: "title"; title: string }
  | { t: "requestCapability"; id: string; kind: CapKind; filter?: CapFilter }
  | { t: "log"; level: LogLevel; msg: string };

/** shell → app */
export type ShellMessage =
  | { t: "mount"; window: WindowInfo; capabilities: GrantedCap[] }
  | { t: "event"; key: string; event: string; payload?: unknown }
  | { t: "capabilityGranted"; id: string; port: MessagePort }
  | { t: "capabilityDenied"; id: string }
  | { t: "unmount" };

export const APP_MESSAGE_TAGS: ReadonlySet<string> = new Set<string>([
  "render",
  "title",
  "requestCapability",
  "log",
]);

export const SHELL_MESSAGE_TAGS: ReadonlySet<string> = new Set<string>([
  "mount",
  "event",
  "capabilityGranted",
  "capabilityDenied",
  "unmount",
]);

/** Marker standing in for a port that rides the transfer list beside the body. */
export type PortRef = { $port: number };

export function isPortRef(value: unknown): value is PortRef {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    typeof (value as { $port?: unknown }).$port === "number"
  );
}

/**
 * An encoded message: a JSON-safe `body` plus the ports it refers to. Maps
 * directly onto `port.postMessage(body, transfer)` and, for remote apps, onto a
 * WebSocket frame with a port table alongside it.
 */
export type Wire = {
  body: Record<string, unknown>;
  transfer: MessagePort[];
};

/** The subset of `MessagePort` the codec needs, so remote bridges can stand in. */
export interface PortLike {
  postMessage(message: unknown, transfer: Transferable[]): void;
}

/** A capability id namespaced to its window, so revocation is per-window. */
export function capId(windowId: string, local: string): CapId {
  return `${windowId}/${local}`;
}
